/**
 * Detect Chrome's "Request desktop site" mode on a phone.
 *
 * WHY: when an Android user enables Chrome's "Desktop site" toggle, Chrome forces
 * the layout viewport to a fixed ~980px and (per Chromium's documented behavior)
 * IGNORES the page's <meta name="viewport">. The whole page then renders at desktop
 * width and is scaled down to fit the screen → everything looks tiny. This setting
 * PROPAGATES into a PWA installed from that Chrome, so the installed app is broken
 * too. The app cannot reliably override it (desktop-site intentionally overrides the
 * page viewport), so the realistic mitigation is to DETECT it and tell the user.
 *
 * DETECTION SIGNATURE (touch phone being forced to a desktop-width viewport):
 *   - innerWidth is implausibly wide for the physical device (>= DESKTOP_FORCED_MIN,
 *     ~960; Chrome forces ~980), AND
 *   - the device is touch-primary (coarse pointer / maxTouchPoints>0) — a real
 *     desktop at that width is fine-pointer / 0 touch points, AND
 *   - (smoking gun, when available) window.screen.width reports a PHYSICAL width
 *     much smaller than innerWidth — a real desktop has screen.width >= innerWidth.
 *
 * ⚠️ UNVERIFIED ON REAL HARDWARE: whether `screen.width` reports the physical device
 * width (e.g. 412) or the forced 980 while desktop-site is on is browser-version
 * dependent and MUST be confirmed on a real Android device (headless can't replicate
 * it). The detector therefore treats the screen.width signal as a BONUS confirmation,
 * not a requirement, and falls back to the touch+wide-innerWidth signature which is
 * robust on its own. Tune SCREEN_RATIO / DESKTOP_FORCED_MIN after real-device verify.
 */

const DESKTOP_FORCED_MIN = 960; // Chrome desktop-site forces ~980; 960 leaves margin
const SCREEN_SMALL_MAX = 820;   // physical phone/tablet screen px ceiling for the bonus signal

export interface DesktopSiteSignals {
  innerWidth: number;
  screenWidth: number;
  devicePixelRatio: number;
  coarsePointer: boolean;
  maxTouchPoints: number;
  uaDataMobile: boolean | null;
  uaHasMobileToken: boolean;
}

export function readSignals(): DesktopSiteSignals {
  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  return {
    innerWidth: window.innerWidth,
    screenWidth: window.screen?.width ?? 0,
    devicePixelRatio: window.devicePixelRatio || 1,
    coarsePointer: typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    uaDataMobile: nav.userAgentData ? (nav.userAgentData.mobile ?? null) : null,
    uaHasMobileToken: /Mobi|Android/i.test(navigator.userAgent),
  };
}

/**
 * True when the current context looks like a touch phone that has been forced to a
 * desktop-width viewport (i.e. Chrome "Desktop site" is on). Conservative: requires
 * BOTH a wide viewport AND touch-primary hardware, so a genuine small-window desktop
 * (fine pointer, no touch) is never flagged.
 */
export function isLikelyDesktopSiteMode(s: DesktopSiteSignals = readSignals()): boolean {
  const wide = s.innerWidth >= DESKTOP_FORCED_MIN;
  if (!wide) return false;

  const touchPrimary = s.coarsePointer || s.maxTouchPoints > 0;
  if (!touchPrimary) return false; // real desktop at >=960px → not flagged

  // Bonus smoking-gun: physical screen far narrower than the forced viewport.
  // (Only strengthens; absence does NOT clear the flag, pending real-device data.)
  const screenBetrays = s.screenWidth > 0 && s.screenWidth <= SCREEN_SMALL_MAX
    && s.innerWidth - s.screenWidth > 100;

  // A high DPR alongside touch is typical of phones; a desktop-site-on phone keeps
  // its real DPR (>1.5 on modern phones) even when the viewport is forced wide.
  const phoneLikeDpr = s.devicePixelRatio >= 1.5;

  // uaData.mobile sometimes stays true even in desktop-site (UA is spoofed but the
  // high-entropy mobile bit can persist); when present+true it's strong corroboration.
  const uaMobileHint = s.uaDataMobile === true || s.uaHasMobileToken;

  // Touch + wide is the core signal. Require at least one corroborating phone signal
  // to avoid flagging touch-screen LAPTOPS (fine+coarse pointer, dpr~1-2, real wide
  // screen) which legitimately want the desktop layout.
  return screenBetrays || phoneLikeDpr || uaMobileHint;
}

/**
 * True when the app is running as an installed/standalone PWA — NOT in a regular
 * browser tab. Reuses the exact detection pattern already established at four other
 * call sites (InstallBanner.tsx, Navbar.tsx, api/client.ts, errorReporter.ts):
 * `matchMedia('(display-mode: standalone)')` for Android/desktop PWA installs, OR'd
 * with the legacy `navigator.standalone` boolean for iOS Safari (which never shipped
 * the display-mode media query). The fuller (both-signal) variant is used here — same
 * as InstallBanner.tsx — rather than the matchMedia-only variant in Navbar.tsx,
 * because the forced-mobile-layout mitigation this gates needs to work correctly on
 * installed iOS PWAs too, not just Android/Chromium ones.
 *
 * Deliberately a FUNCTION, not a module-level `const isPWA = ...` like the other four
 * call sites. Those other sites don't need to vary the result across resettable
 * per-test scenarios and can pay the one-time module-load cost. This one is consumed
 * from two hooks (useForcedMobileLayout, useIsMobile) whose own test suites stub
 * `window.matchMedia` / `navigator.standalone` PER TEST CASE (see test-setup.ts's
 * note on the same top-level-const-vs-import-time gotcha in api/client.ts) — a
 * top-level const here would freeze the answer at module-import time, before any
 * per-test stub is installed, and silently ignore every test's override.
 */
export function isStandalonePWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true;
}
